// Draft text -> HTML, for the rich half of a clipboard copy.
//
// WHY THIS EXISTS AT ALL. A draft may contain markdown links, and only to URLs the
// model was actually shown this turn (see normalizeDraftText, which is what vets
// them). Copying as plain text pastes those literally — an employee receives
// "[HR-118 Paid Time Off](https://…)" in the body of an HR reply, which reads as a
// bug in the email rather than in this app. The rich copy makes them real links.
//
// DUPLICATED FROM backend/src/util/sanitize.ts, deliberately: two npm workspaces
// with no shared package, exactly like types.ts. Duplication is only safe while
// something FAILS when the copies drift, so the block below is delimited by
// sentinel comments and backend/src/util/sanitize.test.ts compares the two byte for
// byte. Edit one, and the suite tells you about the other.

// ===== shared:draftToHtmlBody (keep both copies identical) =====
const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** A raw HTML tag, a markdown link, or a bare URL, in that precedence order.
 *
 *  The tag alternative comes first and consumes a raw `<a href="...">` as ONE
 *  opaque token. Without it, the bare-URL branch below is free to match the URL
 *  sitting inside the href's own quotes and render it as a live, unvetted link —
 *  exactly the model-invented-SharePoint-URL case this function exists to block.
 *  That was masked rather than prevented before: the old bare-URL pattern needed
 *  two separately-quantified matches on the URL body, which happened to fail on
 *  anything shorter than `https://` + 2 characters, so a short test fixture never
 *  exposed it. Neither URL class admits `[`, for a similar reason one level up: a
 *  markdown-link URL that could reabsorb a `[` makes `\[...\]\(...\)` ambiguous
 *  about where the link ends whenever the text has no closing `)` for a while — a
 *  run of `[](http://` repeated made the engine retry a fresh backtrack search at
 *  every occurrence, which is what js/polynomial-redos caught.
 *
 *  Both the tag body (`[^>]`) and the markdown label (`[^\]\n]`) are bounded at
 *  2000 chars rather than left as `*`/`+`. Neither quantifier is ambiguous on its
 *  own, but each is followed by a required literal (`>`, `]`) that a crafted
 *  string can omit entirely, and re-trying that failed match at every subsequent
 *  starting position is quadratic overall -- confirmed by timing a draft made of
 *  `"<a ".repeat(40000)` (no `>` anywhere): the bound turns that from ~1.3s back
 *  to linear. No real tag or link label is anywhere near 2000 characters.
 *
 *  Trailing sentence punctuation ("...see https://x/policy.") is trimmed off the
 *  bare form in `draftToHtmlBody` itself now, not by a second quantified class
 *  here — that second class overlapped the first on everything but `.,;:!?`, the
 *  same shape of ambiguity from the other direction. */
const LINK_TOKEN = /<\/?[a-z][^>]{0,2000}>|\[([^\]\n]{0,2000})\]\(\s*(https?:\/\/[^)\s\[]+)\s*\)|(https?:\/\/[^\s<>"')\]]+)/gi;

/** Plain draft text -> the htmlBody Office.js wants.
 *
 *  Both compose paths used to pass `text.replace(/\n/g, "<br>")` straight through,
 *  which treats the draft as HTML while the pane's preview treats it as plain text.
 *  Anything markup-shaped in the draft therefore rendered ONE way in the preview the
 *  user approves and ANOTHER way in the email that goes out — the mechanism behind a
 *  malformed link in a generated draft. An ordinary sentence was enough to break it:
 *  "salary < $50k" makes the rest of the paragraph a bogus tag.
 *
 *  LINKS (2026-08-26). Two forms become real anchors and nothing else does:
 *    - `[label](https://…)`, which the backend has already vetted — normalizeDraftText
 *      only lets a markdown link keep its URL when that URL appears in the retrieved
 *      materials or the client's FAQ, and reduces every other one to its bare label.
 *    - a bare http(s) URL, which needs no vetting because the user can read exactly
 *      where it goes before they send.
 *
 *  RAW `<a href>` IS STILL ESCAPED TO VISIBLE TEXT, on purpose. That is the model's
 *  own markup and it is the form it invents SharePoint URLs in; letting it through
 *  here would put a live link to a document that may not exist into an email to an
 *  employee. Vetting happens on the server where the material list is; this function
 *  only renders what survived it, and it runs on text a user may have edited, so it
 *  cannot be the thing that decides what is trustworthy.
 *
 *  Tokenised BEFORE escaping rather than after. Escaping first turns `?a=1&b=2` into
 *  `?a=1&amp;b=2`, and an href built from that string is a different URL — the kind of
 *  bug that works on every example without a query string. */
export function draftToHtmlBody(text: string): string {
  const src = String(text);
  let out = "";
  let last = 0;
  LINK_TOKEN.lastIndex = 0;
  for (let m = LINK_TOKEN.exec(src); m; m = LINK_TOKEN.exec(src)) {
    out += esc(src.slice(last, m.index));
    if (m[3] !== undefined) {
      // Bare URL: trim trailing sentence punctuation off the href, but keep it in
      // the visible text — "...see https://x/policy." must not swallow the full
      // stop into the link.
      const trail = /[.,;:!?]+$/.exec(m[3])?.[0] ?? "";
      const url = m[3].slice(0, m[3].length - trail.length);
      out += `<a href="${esc(url)}">${esc(url)}</a>${esc(trail)}`;
    } else if (m[2] !== undefined) {
      const url = m[2];
      const label = m[1] !== undefined && m[1].trim() ? m[1] : url;
      out += `<a href="${esc(url)}">${esc(label)}</a>`;
    } else {
      // A raw HTML tag, most often a model-invented <a href>. Escaped verbatim as
      // one token so its attributes never reach the bare-URL branch above.
      out += esc(m[0]);
    }
    last = m.index + m[0].length;
  }
  out += esc(src.slice(last));
  return out.replace(/\n/g, "<br>");
}
// ===== end shared:draftToHtmlBody =====
